import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Chip,
    FormControlLabel,
    Stack,
    Switch,
    Tab,
    Tabs,
    Typography
} from '@mui/material';
import { collection, getDocs, limit, orderBy, query, Timestamp } from 'firebase/firestore';
import { firestore } from '@api/Firebase/Firebase';
import { axiosPrivate } from '@api/ApiHelper/axiosPrivate';
import { showAlertError } from '@utils/Alerts/Error';

/**
 * BITÁCORA DE ENVÍOS — la D4 del plan de notificaciones.
 *
 * Cada mensaje que SALE (WhatsApp, push, el aviso del pase de Wallet) deja su
 * renglón con el resultado — y cuando falla, **el error que antes moría en un
 * `console.error` que nadie leía queda aquí, a la vista**. Tres avisos se
 * perdieron así en una sola semana; esta pantalla existe para que no haya un
 * cuarto.
 *
 * ## Costo (regla del CLAUDE.md raíz)
 * - Lectura acotada: `limit(200)` por fecha descendente. **Los filtros van EN
 *   CLIENTE**: un `where` + `orderBy` de campos distintos exigiría índice
 *   compuesto (regla del repo: no crearlos).
 * - Al abrir, dispara la RETENCIÓN en el server (fire-and-forget): borra lo
 *   más viejo que 30 días — la lección de los 172k `activity-logs`.
 */

interface LogRow {
    id: string;
    channel: 'whatsapp' | 'webpush' | 'wallet';
    flow: string;
    to: string;
    ok: boolean;
    error?: string;
    info?: string;
    createdAt?: Timestamp;
}

const CANAL: Record<string, { label: string; color: 'success' | 'info' | 'warning' }> = {
    whatsapp: { label: 'WhatsApp', color: 'success' },
    webpush: { label: 'Push', color: 'info' },
    wallet: { label: 'Wallet', color: 'warning' }
};

const cuando = (fecha?: Timestamp): string =>
    fecha
        ? fecha.toDate().toLocaleString('es-MX', {
              day: '2-digit',
              month: 'short',
              hour: '2-digit',
              minute: '2-digit'
          })
        : '';

export const MessageLogView: React.FunctionComponent = () => {
    const [rows, setRows] = useState<LogRow[]>([]);
    const [canal, setCanal] = useState<string>('todos');
    const [soloFallidos, setSoloFallidos] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Retención, sin esperar la respuesta: si el server duerme (escala a
        // cero), despierta con esto y la lista carga en paralelo.
        axiosPrivate.post('/message-log/cleanup').catch(() => undefined);

        const cargar = async () => {
            try {
                const snapshot = await getDocs(
                    query(collection(firestore, 'message-log'), orderBy('createdAt', 'desc'), limit(200))
                );
                setRows(snapshot.docs.map((d) => ({ ...(d.data() as LogRow), id: d.id })));
            } catch (error: any) {
                showAlertError(error);
            }
            setLoading(false);
        };
        void cargar();
    }, []);

    const visibles = useMemo(
        () =>
            rows
                .filter((row) => canal === 'todos' || row.channel === canal)
                .filter((row) => !soloFallidos || !row.ok),
        [rows, canal, soloFallidos]
    );

    const fallidos = useMemo(() => rows.filter((row) => !row.ok).length, [rows]);

    return (
        <Box p={2} sx={{ overflowY: 'auto', height: '100%' }}>
            <Stack spacing={2} maxWidth={900}>
                {/* Canal en TABS y no en select (pedido de Irving): un clic
                    en vez de dos, y se ve de un vistazo qué canales hay. */}
                <Tabs
                    value={canal}
                    onChange={(_, value) => setCanal(value)}
                    variant="scrollable"
                    sx={{ minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0.5 } }}
                >
                    <Tab value="todos" label={`Todos (${rows.length})`} />
                    <Tab value="whatsapp" label={`WhatsApp (${rows.filter((r) => r.channel === 'whatsapp').length})`} />
                    <Tab value="webpush" label={`Push (${rows.filter((r) => r.channel === 'webpush').length})`} />
                    <Tab value="wallet" label={`Wallet (${rows.filter((r) => r.channel === 'wallet').length})`} />
                </Tabs>
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <FormControlLabel
                        control={
                            <Switch
                                checked={soloFallidos}
                                onChange={(event) => setSoloFallidos(event.target.checked)}
                            />
                        }
                        label={`Solo fallidos (${fallidos})`}
                    />
                    <Box flex={1} />
                    <Typography variant="caption" color="text.secondary">
                        Últimos {rows.length} envíos · se conservan 30 días
                    </Typography>
                </Stack>

                {!loading && visibles.length === 0 && (
                    <Typography variant="body2" color="text.secondary" py={4} textAlign="center">
                        {rows.length === 0
                            ? 'Aún no hay envíos registrados: la bitácora arranca con el primer mensaje que salga desde hoy.'
                            : 'Nada con esos filtros.'}
                    </Typography>
                )}

                <Stack spacing={0.5}>
                    {visibles.map((row) => (
                        <Stack
                            key={row.id}
                            direction="row"
                            spacing={1.5}
                            alignItems="flex-start"
                            sx={{
                                p: 1,
                                borderRadius: 1,
                                bgcolor: row.ok ? 'transparent' : 'rgba(211, 47, 47, 0.06)',
                                border: '1px solid',
                                borderColor: row.ok ? 'divider' : 'error.light'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 96, pt: 0.25 }}>
                                {cuando(row.createdAt)}
                            </Typography>
                            <Chip
                                size="small"
                                variant="outlined"
                                color={CANAL[row.channel]?.color || 'info'}
                                label={CANAL[row.channel]?.label || row.channel}
                            />
                            <Box flex={1} minWidth={0}>
                                <Typography variant="body2">
                                    <strong>{row.flow}</strong> → {row.to}
                                    {row.info ? ` · ${row.info}` : ''}
                                </Typography>
                                {!row.ok && (
                                    <Typography variant="caption" color="error.main">
                                        {row.error || 'Falló sin detalle'}
                                    </Typography>
                                )}
                            </Box>
                            {row.ok ? (
                                <Chip size="small" label="OK" color="success" variant="outlined" />
                            ) : (
                                <Chip size="small" label="Falló" color="error" />
                            )}
                        </Stack>
                    ))}
                </Stack>
            </Stack>
        </Box>
    );
};

export default MessageLogView;
